import Link from 'next/link'
import { IconLoader } from '@tabler/icons-react'
import { Tooltip } from 'react-tooltip'
import { useMemo, useState } from 'react'
import { classNames } from 'util/classNames'
import { QuickModal } from './Modals/QuickModal'

interface ButtonProps {
  variant?: 'solid' | 'outline' | 'text' | 'warning'
  color?: 'slate' | 'blue' | 'white' | 'red' | 'yellow'
  className?: string
  href?: string
  disabled?: boolean
  loading?: boolean
  icon?: React.ElementType
  iconPosition?: 'left' | 'right'
  iconSize?:
    | 'h-2.5 w-2.5'
    | 'h-3 w-3'
    | 'h-3.5 w-3.5'
    | 'h-4 w-4'
    | 'h-4.5 w-4.5'
    | 'h-5 w-5'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  tooltip?: string
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void
  children?: React.ReactNode
  confirm?: string
  confirmTitle?: string
  type?: 'button' | 'submit' | 'reset'
  title?: string
}

export function Button({
  variant = 'solid',
  color = 'blue',
  className = '',
  href = '',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  size = 'md',
  onClick,
  tooltip,
  children,
  confirm,
  iconSize: iconSizeProp,
  confirmTitle,
  type = 'submit',
  title,
}: ButtonProps) {
  const IconComponent = icon
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const iconSize = useMemo(() => {
    if (iconSizeProp) {
      return iconSizeProp
    }
    switch (size) {
      case 'xs':
        return 'w-3 h-3'
      case 'sm':
        return 'w-3.5 h-3.5'
      case 'md':
        return 'w-4 h-4'
      case 'lg':
        return 'w-4 h-4'
      case 'xl':
        return 'w-5 h-5'
      default:
        return 'w-4 h-4'
    }
  }, [size])

  const buttonClass = useMemo(() => {
    const getBaseSize = () => {
      switch (size) {
        case 'xs':
          return 'py-[4px] px-2 text-[11px]'
        case 'sm':
          return 'py-[6px] px-2 text-[12px]'
        case 'md':
          return 'py-[6px] px-3 text-[13px]'
        case 'lg':
          return 'py-[8px] px-4 text-sm'
        case 'xl':
          return 'py-4 px-6 text-[14px]'
        default:
          return 'py-[9px] px-3 text-[13px]'
      }
    }

    const getBaseColor = () => {
      if (disabled) {
        return '!bg-gray-300 text-gray-500'
      }

      if (variant === 'outline') {
        if (color === 'blue') {
          return `!bg-transparent border border-toasty-blue-600 text-toasty-blue-600 hover:bg-toasty-blue-100`
        }
        return `!bg-transparent border border-${color}-500 text-${color}-500 hover:bg-${color}-600`
      }

      if (variant === 'warning') {
        return `!bg-slate-500 hover:!bg-red-600 text-white `
      }

      if (variant === 'text') {
        return `!bg-transparent !border-transparent text-${color}-500 hover:!bg-${color}-100 hover:underline`
      }

      switch (color) {
        case 'slate':
          return '!bg-slate-500 text-white hover:!bg-slate-600 border border-slate-500'
        case 'blue':
          return '!bg-toasty-blue-600 hover:!bg-toasty-blue-700 text-white border border-toasty-blue-600'
        case 'white':
          return '!bg-white border border-gray-200 hover:!bg-gray-100 text-gray-800'
        case 'red':
          return '!bg-red-500 text-red-100 border border-red-500'
        case 'yellow':
          return '!bg-yellow-500 hover:!bg-yellow-600 text-white border border-yellow-500'
        default:
          return '!bg-toasty-blue-600 hover:!bg-toasty-blue-700 text-white border border-toasty-blue-600'
      }
    }

    return classNames(
      'rounded-md border group inline-flex items-center justify-center h-fit text-xs font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 transition-all duration-300 ease-in-out overflow-hidden text-overflow-ellipsis whitespace-nowrap',
      className,
      getBaseSize(),
      getBaseColor(),
      disabled && 'opacity-50'
    )
  }, [size, color, className, disabled, variant])

  const content = (
    <span
      title={title}
      className={classNames(
        'flex items-center',
        size === 'xs' ? 'gap-x-0.5' : 'gap-x-1'
      )}
      data-tooltip-id={tooltip}
    >
      {loading && <IconLoader className={`animate-spin ${iconSize}`} />}
      {!loading && iconPosition === 'left' && IconComponent && (
        <IconComponent className={iconSize} />
      )}
      {children && <span className="flex items-center">{children}</span>}
      {!loading && iconPosition === 'right' && IconComponent && (
        <IconComponent className={iconSize} />
      )}
    </span>
  )

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return

    if (confirm) {
      e.preventDefault()
      setShowConfirmModal(true)
    } else {
      if (onClick) onClick(e)
    }
  }

  const buttonElement = href ? (
    disabled || loading ? (
      <span className={buttonClass}>{content}</span>
    ) : (
      <Link href={href} className={buttonClass}>
        {content}
      </Link>
    )
  ) : (
    <button
      type={type}
      className={buttonClass}
      disabled={disabled || loading}
      onClick={handleClick}
    >
      {content}
      {tooltip && (
        <Tooltip style={{ fontSize: '12px' }} content={tooltip} id={tooltip} />
      )}
    </button>
  )

  return (
    <>
      {buttonElement}
      {showConfirmModal && (
        <QuickModal
          open={showConfirmModal}
          setOpen={setShowConfirmModal}
          title={confirmTitle || `Are you sure you want to continue?`}
          description={confirm || ''}
          action="Confirm"
          onAction={() => {
            if (onClick) onClick()
            setShowConfirmModal(false)
          }}
        />
      )}
      {tooltip && (
        <Tooltip style={{ fontSize: '12px' }} content={tooltip} id={tooltip} />
      )}
    </>
  )
}
